/**
 * Paleta de colores para los roles - Optimizada para accesibilidad WCAG AA
 * SINCRONIZADA CON BACKEND (server/utils/colorPalette.js)
 *
 * Todos los colores garantizan un ratio de contraste ≥4.5:1 con texto blanco
 * Esto permite usar texto blanco sobre estos fondos en avatares y badges.
 *
 * @version 4.0.0 - Sistema de variantes de saturación (24 → 48 colores virtuales)
 */
const rolColors = [
  // Paleta original (12 colores)
  '#b34b4b', // Rojo Coral (5.22:1)
  '#2f7b76', // Turquesa (4.98:1)
  '#308092', // Azul Cielo (4.54:1)
  '#7f6c33', // Amarillo Maíz (5.13:1)
  '#5a7c6c', // Verde Menta (4.63:1)
  '#805a51', // Salmón (6.0:1)
  '#71728b', // Pizarra Claro (4.69:1)
  '#836313', // Oro Viejo (5.59:1)
  '#a9621c', // Mandarina (4.72:1)
  '#82666e', // Rosa Viejo (5.15:1)
  '#4a6b8a', // Azul Acero (4.67:1)
  '#6b5b3d', // Bronce (5.21:1)
  
  // Paleta extendida (12 colores adicionales)
  '#8b4789', // Púrpura (4.89:1)
  '#2d6b4f', // Verde Bosque (5.12:1)
  '#9b5c3f', // Terracota (4.68:1)
  '#4d5d7a', // Azul Medianoche (5.34:1)
  '#7a5c42', // Caramelo (5.45:1)
  '#5d7a5d', // Verde Oliva (4.71:1)
  '#8a5a6b', // Vino Rosado (5.18:1)
  '#3d6b8a', // Azul Océano (4.92:1)
  '#7a6b3d', // Mostaza (5.28:1)
  '#6b4d5d', // Ciruela (5.67:1)
  '#4a7a6b', // Verde Jade (4.83:1)
  '#8a6b4d', // Caoba (4.95:1)
];

/**
 * Convierte un color HEX a HSL
 * @param {string} hex - Color en formato #RRGGBB
 * @returns {{h: number, s: number, l: number}} Valores HSL (h: 0-360, s: 0-100, l: 0-100)
 */
function hexToHSL(hex) {
  // Convertir HEX a RGB (0-1)
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // Achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

/**
 * Convierte HSL a HEX
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Color en formato #RRGGBB
 */
function hslToHex(h, s, l) {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // Achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Generar color consistente basado en string (nombre del rol)
 * USA EL MISMO ALGORITMO QUE EL BACKEND para garantizar consistencia
 *
 * SISTEMA DE VARIANTES (v4.0.0):
 * - 24 colores base × 2 variantes = 48 colores virtuales
 * - Variante 0: Color original
 * - Variante 1: Mismo matiz, saturación -25%
 *
 * @param {string} str - String para generar hash (nombre del rol)
 * @returns {string} Color hex
 */
const getColorFromString = (str) => {
  // Validación de entrada
  if (!str || typeof str !== 'string') {
    console.warn('[rolColorUtils] String inválido, usando color por defecto');
    return rolColors[0];
  }

  // Normalizar string (trim y lowercase para consistencia)
  const normalized = str.trim().toLowerCase();
  if (normalized.length === 0) {
    return rolColors[0];
  }

  // Generar hash usando algoritmo djb2 (MISMO QUE BACKEND)
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = ((hash << 5) + hash) + normalized.charCodeAt(i); // hash * 33 + c
  }

  // Seleccionar color base de la paleta (0-23)
  const colorIndex = Math.abs(hash) % rolColors.length;
  const baseColor = rolColors[colorIndex];

  // Generar variante usando segundo nivel de hash (0 o 1)
  const variantHash = Math.floor(Math.abs(hash) / rolColors.length);
  const variantIndex = variantHash % 2;

  // Variante 0: Color original
  if (variantIndex === 0) {
    return baseColor;
  }

  // Variante 1: Reducir saturación 25% (multiplicar por 0.75)
  try {
    const hsl = hexToHSL(baseColor);
    const newSaturation = Math.max(Math.round(hsl.s * 0.75), 20); // Mínimo 20% saturación
    return hslToHex(hsl.h, newSaturation, hsl.l);
  } catch (error) {
    console.warn('[rolColorUtils] Error generando variante, usando color base', error);
    return baseColor;
  }
};

/**
 * Obtener color para rol (compatibilidad con código existente)
 * DEPRECADO: Usar getColorFromString directamente
 * 
 * @param {string|number} indexOrId - Índice o ID del rol
 * @param {Array} existingRoles - Roles existentes (ignorado ahora)
 * @returns {string} Color hex
 */
const getColorForRol = (indexOrId, existingRoles = []) => {
  // Si es un índice numérico
  if (typeof indexOrId === 'number') {
    return rolColors[indexOrId % rolColors.length];
  }
  
  // Si es un string, tratarlo como nombre de rol
  if (typeof indexOrId === 'string') {
    // Buscar rol existente por ID para obtener su nombre
    const existingRol = existingRoles.find(r => r.id === indexOrId);
    if (existingRol && existingRol.name) {
      return getColorFromString(existingRol.name);
    }
    // Si no se encuentra, generar desde el ID
    return getColorFromString(indexOrId);
  }
  
  // Fallback
  return rolColors[0];
};

/**
 * Obtener color para nuevo rol basado en su nombre
 * 
 * @param {string} rolName - Nombre del nuevo rol
 * @param {Array} existingRoles - Roles existentes (no usado, solo compatibilidad)
 * @returns {string} Color hex
 */
const getUniqueColorForNewRol = (rolName, existingRoles = []) => {
  // Si se proporciona nombre, generar color consistente
  if (rolName && typeof rolName === 'string') {
    return getColorFromString(rolName);
  }
  
  // Fallback: primer color disponible que no esté usado
  const usedColors = new Set(
    existingRoles
      .map(rol => rol.color)
      .filter(color => color && typeof color === 'string')
  );
  
  for (const color of rolColors) {
    if (!usedColors.has(color)) {
      return color;
    }
  }
  
  // Si todos están usados, usar el siguiente en el ciclo
  return rolColors[existingRoles.length % rolColors.length];
};

/**
 * Validar que un color sea hex válido
 * @param {string} color - Color a validar
 * @returns {boolean} True si es válido
 */
const isValidHexColor = (color) => {
  if (typeof color !== 'string') return false;
  return /^#[0-9A-F]{6}$/i.test(color);
};

/**
 * Validar y generar color si es inválido
 * @param {string} color - Color a validar
 * @param {string} fallbackName - Nombre para generar color
 * @returns {string} Color hex válido
 */
const validateOrGenerateColor = (color, fallbackName = '') => {
  // Si el color es válido, usarlo
  if (isValidHexColor(color)) {
    return color;
  }
  
  // Si hay nombre de fallback, generar color
  if (fallbackName && typeof fallbackName === 'string') {
    return getColorFromString(fallbackName);
  }
  
  // Último recurso: primer color de la paleta
  return rolColors[0];
};

// Helper to get initials for Avatar
// Skips stop words (prepositions, articles) so "Gestor de Sharepoint" → "GS" not "GD"
const INITIALS_STOP_WORDS = new Set([
  'de', 'del', 'la', 'el', 'los', 'las', 'y', 'e', 'a', 'en', 'con', 'por', 'para', 'o', 'u', 'al',
  'of', 'the', 'and', 'in', 'on', 'at', 'to', 'for',
]);

const getInitials = (name) => {
  if (!name) return 'R'; // Default R for Role
  // "Sofía — Guía DELE Escolar" and "Guía DELE Escolar — Sofía" → same initials:
  // take the longer segment (role name is always longer than persona name)
  const normalized = name.includes(' — ')
    ? name.split(' — ').reduce((a, b) => a.length >= b.length ? a : b)
    : name;
  const words = normalized.trim().split(/\s+/);
  const significant = words.filter(w => !INITIALS_STOP_WORDS.has(w.toLowerCase()));
  const chosen = significant.length > 0 ? significant : words;
  if (chosen.length > 1) {
    return `${chosen[0][0]}${chosen[1][0]}`.toUpperCase();
  }
  return chosen[0].substring(0, 2).toUpperCase();
};

export {
  // Paleta
  rolColors,

  // Funciones principales
  getColorFromString,      // v4.0.0: Incluye sistema de variantes (24 → 48 colores)
  getColorForRol,          // Mantenida por compatibilidad
  getUniqueColorForNewRol, // Actualizada para usar getColorFromString

  // Conversión de colores
  hexToHSL,
  hslToHex,

  // Validación
  isValidHexColor,
  validateOrGenerateColor,

  // Utilidades
  getInitials
};
